import {
  Box,
  Card,
  CardActions,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import { useAuthError, useSignIn } from "../../auth";
import BarcodeScannerField from "../../components/BarcodeScannerField";
import ErrorMessage from "../../components/ErrorMessage";
import SubmitButton from "../../components/SubmitButton";
import { ErrorLike } from "../../types";
import Settings from "./Settings";

type Values = { token: string; serverUrl: string };

const initialValues: Values = { token: "", serverUrl: "" };

export default function SignIn() {
  const signIn = useSignIn();
  const error = useAuthError();
  const { t } = useTranslation("welcome");

  const schema = useMemo(
    () =>
      object().shape({
        serverUrl: string(),
        token: string().required(t("signin.licenseKey.required")),
      }),
    [t],
  );

  const onSubmit = useCallback(
    async (
      { token, serverUrl }: Values,
      { setSubmitting, setStatus }: FormikHelpers<Values>,
    ) => {
      try {
        setStatus(undefined);

        if (serverUrl) {
          throw new Error(
            `Error connecting to rescueTABLET server at ${serverUrl}`,
          );
        }

        await signIn(token);
      } catch (error) {
        setStatus(error);
      } finally {
        setSubmitting(false);
      }
    },
    [signIn],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      <FormContent error={error} />
    </Formik>
  );
}

function FormContent({ error }: { error?: ErrorLike }) {
  const { t } = useTranslation("welcome");
  const { status, isValid, dirty, isSubmitting, setFieldValue, submitForm } =
    useFormikContext();
  const loginCode = useLoginCode();

  useEffect(() => {
    if (loginCode) {
      setFieldValue("token", loginCode);
      setTimeout(submitForm, 0);
    }
  }, [loginCode, setFieldValue, submitForm]);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Form noValidate>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">{t("signin.title")}</Typography>
            {error ? (
              <ErrorMessage error={error} card />
            ) : (
              <Typography>{t("signin.info")}</Typography>
            )}
            {status && <ErrorMessage error={status} card />}
            <Box sx={{ pt: 1 }}>
              <BarcodeScannerField
                name="token"
                label={t("signin.licenseKey.label")}
                required
                fullWidth
                disabled={isSubmitting}
              />
              <Box sx={{ textAlign: "right" }}>
                <Settings />
              </Box>
            </Box>
          </Stack>
        </CardContent>
        <CardActions>
          <SubmitButton
            fullWidth
            disabled={!dirty || !isValid}
            loading={isSubmitting}
          >
            {t("signin.action")}
          </SubmitButton>
        </CardActions>
      </Form>
    </Card>
  );
}

function useLoginCode() {
  return new URLSearchParams(window.location.search).get("key");
}
